import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import validateFileSize from "common/validate-file-size";
import serveRequestErrors from "common/serve-request-errors";
import converReadableFileSize from "common/convert-readable-file-size";
import { CALENDAR_FORMAT, formatDatePattern } from "common/calendar";
import AppButton from "components/app-button";
import AppCalendarInput from "components/app-calendar-input";
import uploadIcon from "assets/images/upload-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";
import placeholderFormat from "common/placeholder-format";
import AppButtonInput from "components/app-button-input";
import COMMON from "common";


const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

export const AppUploadCertModal = (props, ref) => {
    const dispatch = useDispatch();
    const uploadInputRef = useRef();
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const minDate = useMemo(() => new Date(currentYear, 0, 1), [currentYear]);
    const [id, setId] = useState();
    const [disableFields, setDisableFields] = useState(false);
    const [visible, setVisible] = useState(false);
    const initialValues = useMemo(() => ({ status: "", file: "", expiryDate: "" }), []);

    //prettier-ignore
    const formik = useFormik({
        initialValues,
        validationSchema: yup.object({
            status: yup.string().required(ERRORS.REQUIRED),
            file: yup.mixed().nullable().test("fileSize", ERRORS.FILE_SIZE, validateFileSize),
        }),
        onSubmit: (values) => {
            onHandleConfirm(values);
        },
    });
    const fileSize = useMemo(() => formik.values?.file?.size, [formik.values]);

    const onHandleShow = useCallback((data) => {
        setId(data);
        setVisible(true);
    }, []);

    const onHandleDismiss = useCallback(() => {
        setVisible(false);
    }, []);

    const onHandleUploadFile = useCallback(() => {
        uploadInputRef.current.click();
    }, []);

    //prettier-ignore
    const onHandleChange = useCallback((event) => {
        const file = event.target.files[0];
        formik.setFieldValue("file", file);
    }, [formik]);

    //prettier-ignore
    const onHandleRemoveFile = useCallback(() => {
        formik.setFieldValue("file", null);
        uploadInputRef.current.value = "";
    }, [formik]);

    //prettier-ignore
    const statusSelection = useCallback((name, value) => {
        formik.setFieldValue(name, value);

        if (value === COMMON.STATUS_ID.FAILED) {
            formik.setFieldValue("file", null);
            formik.setFieldValue("expiryDate", null);

            setDisableFields(true);
        } else {
            setDisableFields(false);
        }
    }, [formik]);

    //prettier-ignore
    const onHandleUploadDocument = useCallback(async (values) => {
        if (!values.file) return true;
        try {
            const formData = new FormData();
            formData.append("file", values.file);
            if (values.expiryDate) {
                formData.append("expiryDate", formatDatePattern(new Date(values.expiryDate), CALENDAR_FORMAT.CALENDAR_FORMAT));
            }
            await api.post.assignment.uploadDocument(id, formData);
            return true;
        } catch (error) {
            serveRequestErrors(error);
            return false;
        }
    }, [id]);

    //prettier-ignore
    const onHandleConfirm = useCallback(async (values) => {
        try {
            const uploadSuccess = await onHandleUploadDocument(values);
            if (!uploadSuccess) return;

            await api.post.assignment.updateStatus(id, values.status);

            setVisible(false);
            formik.resetForm();
            dispatch(promptAlertMessage({
                message: "Certificate has been submitted successfully"
            }));
            props.onHandleGetList();
        } catch (error) {
            serveRequestErrors(error);
            setVisible(false);
            formik.resetForm();
        }

    }, [id, onHandleUploadDocument, dispatch, props, formik]);

    //prettier-ignore
    useImperativeHandle(ref, () => ({
        onHandleShow: onHandleShow,
        onHandleDismiss: onHandleDismiss,
    }));

    return (
        <Modal classes={{ root: "app-upload-cert-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="upload-cert-modal" aria-describedby="upload-cert-modal">
            <div className="upload-cert-modal">
                <div className="upload-cert-modal__main">
                    <div className="upload-cert-modal__header">
                        <p className="upload-cert-modal__title">Upload Certificate</p>
                        <div className="upload-cert-modal__note">
                            Note:
                            <ol>
                                <li>Please ensure the expiry date is correct if entered</li>
                            </ol>
                        </div>
                    </div>

                    <form className="upload-cert-modal__body" onSubmit={formik.handleSubmit}>
                        <AppButtonInput required name="status" label="Status" value={formik.values.status} disabled={formik.isSubmitting} options={COMMON.DROPDOWNS.EXAM_STATUS} error={formik.errors.status} touched={formik.touched.status} onChange={(name, value) => statusSelection(name, value)} />

                        <div className="upload-cert-modal__upload-input">
                            {/* prettier-ignore */}
                            <label className="app-input__label" htmlFor="attachment">Attachment</label>
                            <p className="upload-cert-modal__description">Please ensure that your file is in the correct format (jpg, jpeg, png, pdf) with each file size not exceeding 5MB.</p>

                            <div className="upload-cert-modal__input">
                                {formik.values?.file && (
                                    <button type="button" disabled={disableFields || formik.isSubmitting} className="upload-cert-modal__upload-button" onClick={onHandleUploadFile}>
                                        {formik.values?.file?.name}
                                        <img className="upload-cert-modal__icon" src={uploadIcon} alt="upload" />
                                    </button>
                                )}

                                {!formik.values?.file && (
                                    <button type="button" disabled={disableFields || formik.isSubmitting} className="upload-cert-modal__upload-button" onClick={onHandleUploadFile}>
                                        Upload
                                        <img className="upload-cert-modal__icon" src={uploadIcon} alt="upload" />
                                    </button>
                                )}

                                <p className="upload-cert-modal__label">{converReadableFileSize(fileSize, true) || "-"}</p>

                                {formik.values?.file && (
                                    <button type="button" className="upload-cert-modal__delete" disabled={disableFields || formik.isSubmitting} onClick={onHandleRemoveFile}>
                                        <img src={removeIcon} alt="remove" />
                                    </button>
                                )}

                                <input type="file" name="file" accept="image/jpeg, image/jpg, image/png, application/pdf" hidden ref={uploadInputRef} onChange={onHandleChange} />
                            </div>

                            {formik.touched.file && formik.errors.file && <div className="upload-cert-modal__error">{formik.errors.file}</div>}
                        </div>
                        {/* prettier-ignore */}
                        <AppCalendarInput name="expiryDate" label="Expiry Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={minDate} value={formik.values.expiryDate} disabled={disableFields || formik.isSubmitting} onChange={formik.setFieldValue} />

                        <div className="upload-cert-modal__button-container">
                            <AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
                            <AppButton type="submit" label="Submit" disabled={formik.isSubmitting} />
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    );
};

export default memo(forwardRef(AppUploadCertModal));

AppUploadCertModal.propTypes = {
    ref: PropTypes.object,
};
